import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import gql from "graphql-tag"; // eslint-disable-line import/no-named-as-default
import { print } from "graphql/language/printer";

import Head from "next/head";

import { publicConfig, trackEvent, EVENT_CATEGORY } from "@lululemon/ecom-core";

import { graphql } from "@lululemon/dsp-shared";
import { sentryLog } from "@lululemon/dsp-shared/src/logger";
import cs from "classnames";
import experientialStoreQuery from "../src/graphql/queries/experiential-store";
import styles from "./store-locator.module.scss";
import { getRegionalizedTitleTag, useRegionalize } from "@lululemon/mwa-i18n";

import { StoreLocatorWrapper } from "./components/store-locator-wrapper/store-locator-wrapper";
import { storeLocatorMeta } from "./content/generateMetaData";

const storeListQuery = print(gql`
  query {
    storeLocatorList {
      city
      country
      fullAddress
      hours {
        name
        openHour
        closeHour
      }
      id
      latitude
      longitude
      message
      name
      slug
      specialHours {
        date
        description
        openHour
        closeHour
      }
      state
      storeType
      storeStatus
      storeLocatorAmenities {
        footwear
        hasMirror
        personalShopping
        likeNew
        buyOnlinePickupStore
        curbsidePickup
        complementaryHemming
        returnsExchanges
      }
    }
  }
`);

export const StoreLocator = (props) => {
  const { req, res, meta = {}, nextLocale } = props;

  const [storeListData, setStoreListData] = useState([]);
  const { regionalizeLink } = useRegionalize();

  useEffect(() => {
    trackEvent(EVENT_CATEGORY.PAGE_VIEW);
  }, []);

  useEffect(() => {
    const getStoreList = async () => {
      const responseList = await graphql({
        query: storeListQuery,
        req,
        res,
        getSecret: req?.app?.locals?.getSecret
      });

      return await Promise.all(
        responseList?.data?.storeLocatorList.map(async (data) => {
          if (data.storeType === "experiential") {
            const expRes = await graphql({
              query: experientialStoreQuery,
              variables: {
                storeId: data.id
              },
              req,
              res,
              getSecret: req?.app?.locals?.getSecret
            });

            return {
              ...data,
              experientialStoreData: expRes?.data?.storeInformationById
            };
          }
          return data;
        })
      );
    };

    getStoreList().then((stores) => {
      if (stores) {
        setStoreListData(stores);
      }
    });
  }, [req, res]);

  const canonicalLink = `${publicConfig.nav.apiHost}${meta.canonicalPath}`;

  const regionalizedCanonical = regionalizeLink(canonicalLink);

  const regionalizedTitleTag = getRegionalizedTitleTag(nextLocale, meta.title);

  return (
    <>
      <Head>
        <title>{regionalizedTitleTag}</title>
        <link rel="canonical" href={regionalizedCanonical} />
        {meta?.hrefLangEntries?.map((link) => (
          <link
            key={link.hreflang}
            rel={link.rel}
            href={link.href}
            hrefLang={link.hreflang}
          />
        ))}
        <meta name="description" content={meta.description} />
        <meta name="robots" content={meta.robots} />
      </Head>
      <main className={cs("lll-grid", styles.storeLocatorContainer)}>
        <StoreLocatorWrapper
          storeListData={storeListData}
          publicConfig={publicConfig}
          showAllStoresBtn
        />
      </main>
    </>
  );
};

StoreLocator.getInitialProps = async (ctx) => {
  const { nextLocale } = ctx;
  try {
    return {
      meta: storeLocatorMeta(),

      nextLocale
    };
  } catch (error) {
    sentryLog(error, {
      errorInfo: error,
      tags: {
        componentName: "stores mfe store-locator-page",
        action: "getInitialProps"
      }
    });
  }
};

StoreLocator.propTypes = {
  res: PropTypes.shape({}),
  req: PropTypes.shape({
    app: PropTypes.shape({
      locals: PropTypes.shape({
        getSecret: PropTypes.shape({})
      })
    })
  }),
  meta: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    canonicalPath: PropTypes.string,
    robots: PropTypes.string
  })
};

StoreLocator.defaultProps = {
  res: {},
  req: {
    headers: {}
  },
  meta: {}
};

export default StoreLocator;
